import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { readLegacyFile, downloadLegacyFile } from '@/pages/legacy/shared';
import { UPLOAD_PROFILE_MINUTES, DOWNLOAD_PROFILE_MINUTES } from './queries';
import type { ProfileRecord } from './types';

export const ProfileMinutes = ({ odrnumber, record, canWrite, onSaved }: {
  odrnumber: number; record: ProfileRecord; canWrite: boolean; onSaved: (warnings: string[]) => Promise<unknown>;
}) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{ files: FileList }>();
  const [upload] = useMutation<{ uploadParticipantProfileMinutes: { success: boolean; warnings?: string[] | null } | null }>(UPLOAD_PROFILE_MINUTES);
  const [download, downloading] = useLazyQuery<{ downloadParticipantProfileMinutes: { filename: string; content: string; contentType: string } | null }>(DOWNLOAD_PROFILE_MINUTES, { fetchPolicy: 'network-only' });
  const id = `profile-minutes-${record.key.replace(/\W/g, '_')}`;
  const submit = handleSubmit(async values => {
    const file = values.files[0];
    setPending(true); setError(null);
    try {
      const content = await readLegacyFile(file);
      const result = await upload({ variables: { odrnumber, key: record.key, revision: record.revision, filename: file.name, content } });
      if (result.data?.uploadParticipantProfileMinutes?.success !== true) throw new Error('Unable to upload Court Minutes.');
      await onSaved(result.data.uploadParticipantProfileMinutes.warnings ?? []);
      reset(); setOpen(false);
    } catch (error) { setError(error instanceof Error ? error.message : 'Unable to upload Court Minutes.'); }
    finally { setPending(false); }
  });
  const downloadFile = async () => {
    setError(null);
    const preview = window.open('about:blank', '_blank');
    if (!preview) { setError('Unable to open Court Minutes.'); return; }
    preview.opener = null;
    try {
      const result = await download({ variables: { odrnumber, key: record.key } });
      if (result.error) throw result.error;
      if (!result.data?.downloadParticipantProfileMinutes) throw new Error('Unable to download Court Minutes.');
      downloadLegacyFile(result.data.downloadParticipantProfileMinutes, preview);
    } catch (error) { preview.close(); setError(error instanceof Error ? error.message : 'Unable to download Court Minutes.'); }
  };
  return <div className="space-y-2">
    {error && !open && <p role="alert">{error}</p>}
    <div className="flex gap-2">
      {record.values.some(value => value.name === 'filename' && value.value) && <Button id={`${id}-download`} variant="outline" disabled={downloading.loading} onClick={() => { void downloadFile(); }}><Download aria-hidden="true" className="mr-2 h-4 w-4" />Court Minutes</Button>}
      {canWrite && <Button id={`${id}-open-upload`} variant="outline" onClick={() => { setError(null); setOpen(true); }}><Upload aria-hidden="true" className="mr-2 h-4 w-4" />Attach Court Minutes</Button>}
    </div>
    <Dialog open={open} onOpenChange={(value: boolean) => { if (!pending) setOpen(value); }}><DialogContent aria-describedby={undefined} onInteractOutside={event => event.preventDefault()}><DialogHeader><DialogTitle>Attach Court Minutes</DialogTitle></DialogHeader>
      <form id={`${id}-form`} className="space-y-4" onSubmit={submit}>
        {error && <p role="alert">{error}</p>}
        <div><Label htmlFor={`${id}-file`}>Court Minutes PDF</Label><Input id={`${id}-file`} className="h-8" type="file" accept="application/pdf,.pdf" {...register('files', { validate: files => Boolean(files?.[0]) && /\.pdf$/i.test(files[0].name) && files[0].size <= 32 * 1024 * 1024 })} /></div>
        {errors.files && <p role="alert">Choose a PDF of 32 MB or less.</p>}
        <div className="flex justify-end gap-2"><Button id={`${id}-upload`} type="submit" disabled={pending}><Upload aria-hidden="true" className="mr-2 h-4 w-4" />Upload</Button><Button id={`${id}-cancel`} type="button" variant="outline" disabled={pending} onClick={() => setOpen(false)}>Cancel</Button></div>
      </form>
    </DialogContent></Dialog>
  </div>;
};
